import { useFetchQuoteCountInGroup } from '@uturn/api/v2';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UpdateFilters } from '../multi-update-dialog';

type Params = {
	groupStatus: 'assigned' | 'unassigned';
	isImpactField: boolean;
	isQuoteImpactField: boolean;
	isUnpublish?: boolean;
	totalShipments: number;
	groupId: number;
	filters: UpdateFilters;
};

export const useMultiUpdateConfirmationContent = ({
	groupStatus,
	isImpactField,
	isQuoteImpactField,
	isUnpublish = false,
	totalShipments,
	groupId,
	filters,
}: Params) => {
	const { t } = useTranslation();
	const { data: quotedResp } = useFetchQuoteCountInGroup(groupId, {
		status: groupStatus,
		...filters,
	});

	const quotedCount = quotedResp?.data ?? 0;
	const hasQuotes = quotedCount > 0;

	const title = useMemo(() => {
		if (isUnpublish) {
			return hasQuotes
				? t(
						'pages.multi-update-shipments.confirmation-dialog.unpublish-with-quotes.title',
						'Confirm unpublish, existing quotes will be lost',
					)
				: t(
						'pages.multi-update-shipments.confirmation-dialog.unpublish.title',
						'Confirm unpublish',
					);
		}
		if (groupStatus === 'assigned' && isImpactField) {
			return t(
				'pages.multi-update-shipments.confirmation-dialog.assigned-shipments.title',
				'Confirm multi-update request',
			);
		}
		if (hasQuotes && isQuoteImpactField) {
			return t(
				'pages.multi-update-shipments.confirmation-dialog.unassigned-quoted-shipments.title',
				'Confirm multi-update, existing quotes will be removed',
			);
		}
		return t(
			'pages.multi-update-shipments.confirmation-dialog.default.title',
			'Confirm multi-update',
		);
	}, [
		groupStatus,
		hasQuotes,
		isImpactField,
		isQuoteImpactField,
		isUnpublish,
		t,
	]);

	const description = useMemo(() => {
		if (isUnpublish) {
			return hasQuotes
				? t(
						'pages.multi-update-shipments.confirmation-dialog.unpublish-with-quotes.description',
						'All quotes will be lost and the status will be set to draft. When you republish the shipments, carriers that quoted before will be notified. Do you wish to proceed?',
					)
				: t(
						'pages.multi-update-shipments.confirmation-dialog.unpublish.description',
						'The selected shipments move back to Drafted and are no longer visible to carriers. Do you wish to proceed?',
					);
		}
		if (groupStatus === 'assigned' && isImpactField) {
			return t(
				'pages.multi-update-shipments.confirmation-dialog.assigned-shipment.description',
				'This update might impact the price for the shipment. The carrier must approve this request. Are you sure you want to apply these changes to the selected shipments?',
			);
		}
		if (hasQuotes && isQuoteImpactField) {
			return t(
				'pages.multi-update-shipments.confirmation-dialog.unassigned-quoted-shipments.description',
				'This update may impact existing quotes from carriers. If you continue, the current quotes will be removed and carriers will be notified to place new quotes. Do you wish to proceed?',
			);
		}
		return t(
			'pages.multi-update-shipments.confirmation-dialog.default.description',
			'Are you sure you want to apply these changes to the selected shipments?',
		);
	}, [
		groupStatus,
		hasQuotes,
		isImpactField,
		isQuoteImpactField,
		isUnpublish,
		t,
	]);

	const confirmLabel = useMemo(() => {
		if (isUnpublish) {
			return t(
				'pages.multi-update-shipments.dialog.unpublish.confirm',
				'Unpublish {{count}} shipments',
				{ count: totalShipments },
			);
		}
		if (groupStatus === 'assigned' && isImpactField) {
			return t(
				'pages.multi-update-shipments.dialog.assigned-shipment-confirmation.confirm',
				'Send request',
			);
		}
		return t(
			'pages.multi-update-shipments.dialog.confirmation.confirm',
			'Update {{count}} shipments',
			{ count: totalShipments },
		);
	}, [groupStatus, isImpactField, isUnpublish, totalShipments, t]);

	const cancelLabel = useMemo(
		() =>
			t('pages.multi-update-shipments.dialog.confirmation.cancel', 'Cancel'),
		[t],
	);

	return { title, description, confirmLabel, cancelLabel };
};
